import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";
import { PUB, postJSON, inr } from "./fq";

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-sm font-semibold text-[#3A0A06]">{label}</Label>{children}</div>
);

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID().replace(/-/g, "") : `${Date.now()}${Math.random().toString(16).slice(2)}`);

export default function EnquiryForm({ config, result }) {
  const [c, setC] = useState({ name: "", phone: "", email: "", city: config.location.city || "", country: "India", follow_up_method: "Phone", message: "", alternative_email: "" });
  const [consent, setConsent] = useState({ enquiry_contact: false, acknowledge_indicative: false, marketing: false });
  const [hp, setHp] = useState("");
  const [state, setState] = useState({ s: "idle" });
  const key = useRef(newKey());
  const set = (k, v) => setC({ ...c, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setState({ s: "loading" });
    try {
      const d = await postJSON(`${PUB}/enquiries`, { inputs: config, contact: c, consent, idempotency_key: key.current, website: hp });
      setState({ s: "done", ref: d.enquiry_id, quote: d.quote_id });
    } catch (err) {
      setState({ s: "error", msg: err.message });
    }
  };

  if (state.s === "done") return (
    <div className="fq-panel text-center py-12" data-testid="enquiry-success">
      <CheckCircle2 className="w-12 h-12 mx-auto text-[#2F6B3A]" />
      <h3 className="font-display text-2xl mt-3">Enquiry received</h3>
      <p className="mt-2">Your reference is <b data-testid="enquiry-reference">{state.ref}</b> (quote {state.quote}, revision 1).</p>
      <p className="text-sm opacity-80 mt-2 max-w-lg mx-auto">Our franchise team will contact you by your preferred method. This does not award a franchise, reserve a territory or create any payment obligation.</p>
    </div>
  );

  const A = result.sections.A, B = result.sections.B;
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="enquiry-form">
      <div className="fq-panel text-sm" data-testid="enquiry-quote-summary">
        <b>{result.format.label} · {result.model.label}</b> — known one-time {inr(A.known_subtotal)}{A.has_pending ? " + items awaiting confirmation" : ""}; known fixed monthly {inr(B.known_subtotal)} (before applicable taxes). The exact quote you reviewed is stored with your enquiry.
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <F label="Full name *"><Input required data-testid="enq-name" value={c.name} onChange={(e) => set("name", e.target.value)} /></F>
        <F label="Phone *"><Input required data-testid="enq-phone" type="tel" value={c.phone} onChange={(e) => set("phone", e.target.value)} /></F>
        <F label="Email"><Input data-testid="enq-email" type="email" value={c.email} onChange={(e) => set("email", e.target.value)} /></F>
        <F label="City"><Input data-testid="enq-city" value={c.city} onChange={(e) => set("city", e.target.value)} /></F>
        <F label="Country of residence"><Input data-testid="enq-country" value={c.country} onChange={(e) => set("country", e.target.value)} /></F>
        <F label="Preferred follow-up">
          <div className="flex gap-2" role="radiogroup">
            {["Phone", "WhatsApp", "Email"].map((m) => (
              <button type="button" key={m} aria-pressed={c.follow_up_method === m} data-testid={`enq-followup-${m.toLowerCase()}`} onClick={() => set("follow_up_method", m)} className={`fq-choice fq-pill ${c.follow_up_method === m ? "is-active" : ""}`}>{m}</button>
            ))}
          </div>
        </F>
        {config.responses.domain_email === 0 && <F label="Alternative contact email for the outlet (optional)"><Input data-testid="enq-alt-email" type="email" value={c.alternative_email} onChange={(e) => set("alternative_email", e.target.value)} /></F>}
      </div>
      <F label="Message"><Textarea data-testid="enq-message" rows={4} value={c.message} onChange={(e) => set("message", e.target.value)} /></F>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden="true" />
      <div className="space-y-3">
        {[
          ["enquiry_contact", "I agree that Purnabramha may contact me about this franchise enquiry using the details above. *"],
          ["acknowledge_indicative", "I understand this quote is indicative: CAPEX and deposits are excluded from the fee subtotals, variable costs are payable separately, and it does not award a franchise, promise returns, reserve a territory or create a payment or binding acceptance. *"],
          ["marketing", "Optional: send me occasional Purnabramha updates and offers."],
        ].map(([k, t]) => (
          <label key={k} className="flex items-start gap-3 cursor-pointer text-sm">
            <Checkbox data-testid={`enq-consent-${k}`} checked={consent[k]} onCheckedChange={(v) => setConsent({ ...consent, [k]: !!v })} className="mt-0.5" />
            <span>{t}</span>
          </label>
        ))}
        <p className="text-xs opacity-70">Read our <Link to="/franchise/privacy" target="_blank" className="underline" data-testid="enq-privacy-link">privacy notice</Link>.</p>
      </div>
      {state.s === "error" && <div className="fq-callout danger" data-testid="enquiry-error">{state.msg}. Your selections are kept — please try again.</div>}
      <Button type="submit" className="fq-cta" disabled={state.s === "loading" || !consent.enquiry_contact || !consent.acknowledge_indicative} data-testid="enq-submit">
        {state.s === "loading" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Submit enquiry
      </Button>
    </form>
  );
}
